import Head from 'next/head'
import { useRouter } from 'next/router'

import { useTheme } from 'lib/themes/use-theme'
import { WEBSITE_URL } from 'lib/utils/browser'

import { FCC } from 'types/react'

export type GrafbaseHeadProps = {
  title?: string
  ogTitle?: string
  description?: string | false
  siteName?: string
  imageUrl?: string | false
  keywords?: string
  displayOgImage?: boolean
}

const GrafbaseHead: FCC<GrafbaseHeadProps> = props => {
  const { asPath } = useRouter()
  const {
    title = 'Grafbase',
    description = 'Compose all your data sources into a unified graph. At the edge close to your users or on premise in your infrastructure.',
    siteName = 'Grafbase',
    imageUrl,
    keywords = 'Grafbase, GraphQL, serverless, API, backend, edge, federation, microservices',
    children,
    ogTitle,
    displayOgImage = true,
  } = props

  const { isDarkTheme } = useTheme()
  const favicon = isDarkTheme ? '/favicon-white.png' : '/favicon.png'

  const finalTitle =
    title !== 'Grafbase' && !title.includes('Grafbase')
      ? `${title} – Grafbase`
      : title

  const pathname = asPath === '/' ? '' : asPath.split('?')[0]
  const prodUrl = 'https://grafbase.com' + pathname
  const currentUrl = WEBSITE_URL + pathname
  const finalImageUrl =
    WEBSITE_URL +
    (imageUrl ?? `/api/og?title=${encodeURIComponent(ogTitle || title)}`)

  return (
    <Head>
      <link rel="shortcut icon" type="image/png" href={favicon} />
      <title key="title">{finalTitle}</title>
      {!!description && <meta name="description" content={description} />}

      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      {!!description && (
        <meta property="og:description" content={description} />
      )}
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={siteName} />
      {displayOgImage && <meta property="og:image" content={finalImageUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      {!!description && (
        <meta name="twitter:description" content={description} />
      )}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:site" content={siteName} />
      {displayOgImage && <meta name="twitter:image" content={finalImageUrl} />}

      <link rel="canonical" href={prodUrl} />

      <meta name="keywords" content={keywords} />

      <meta name="application-name" content={title} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={title} />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />

      <link
        rel="apple-touch-icon"
        href="/images/icons/maskable-icon-192x192.png"
      />

      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {children}
    </Head>
  )
}

export default GrafbaseHead
